import { CartSupplement } from '../models/supplement.model';
import { cartLineKey } from './supplement-pricing';

/**
 * What a menu tile's `+` / `−` actually does for a product that offers add-ons.
 *
 * The web analogue of iOS `SupplementTileRouting.swift`. There the branch lived twice, once per
 * tile view, and the two tiles differ only in layout — having the badge read one rule while the
 * action read another is precisely how a tile ended up opening a sheet with no picker in it. Here
 * the menu grid, the category list and the most-ordered strip would each grow their own copy, so
 * it lives here once.
 *
 * Pure: it decides, the caller acts. Nothing in this file imports Angular or touches the DOM,
 * because "which line does this button mean" is a question about the cart's contents, not about a
 * component — and the same question is asked from a template binding, a click handler and a test.
 */

export type SupplementTileIntent = 'increment' | 'decrement' | 'reset';

/**
 * The slice of a cart line this file needs. `CartService.CartItem` satisfies it structurally, so
 * the routing rules stay independent of the cart service (which is what keeps them Angular-free).
 */
export interface SupplementTileLine {
  /** The PRODUCT id — a line is identified by this plus its add-on selection, not by this alone. */
  id: number;
  quantity: number;
  supplements?: CartSupplement[];
}

/**
 * What the caller should do.
 *
 * A discriminated union rather than callbacks: every branch below is a decision the tile can then
 * apply with its own availability checks and its own sheet plumbing, and a test can assert the
 * decision without rendering anything.
 */
export type SupplementTileAction<T extends SupplementTileLine = SupplementTileLine> =
  /** Not an add-on product. Run the tile's existing handler, untouched. */
  | { kind: 'fallback' }
  /** Ask the guest which selection they mean (or which one to add). */
  | { kind: 'openSheet' }
  /** Unambiguous: act on this exact line. `delta` of 'reset' takes the whole line to zero. */
  | { kind: 'adjustLine'; line: T; delta: 1 | -1 | 'reset' }
  /** Nothing to do — the button that produced this intent is not rendered in this state. */
  | { kind: 'none' };

/**
 * The distinct lines a product currently occupies, sorted on the line key.
 *
 * The sort is what makes every surface list combinations in the same order, and — more to the
 * point — keeps that order STABLE while a stepper is being tapped: insertion order would reshuffle
 * the list under the guest's finger the moment a line's quantity changed and it was re-added.
 */
export function productCartLines<T extends SupplementTileLine>(
  cartItems: T[],
  productId: number
): T[] {
  return cartItems
    .filter(item => item.id === productId && item.quantity > 0)
    .sort((a, b) => {
      // Plain code-unit comparison, not localeCompare: iOS sorts these keys with `<` and a
      // locale-aware collation would order the same two combinations differently on the two
      // platforms, for no benefit — a line key is machine text, never read by a guest.
      const left = cartLineKey(a.id, a.supplements);
      const right = cartLineKey(b.id, b.supplements);
      return left < right ? -1 : left > right ? 1 : 0;
    });
}

/**
 * Decide what a tile's stepper should do.
 *
 * `hasSupplements` comes from `SupplementCatalogService.productHasSupplements` — passed in rather
 * than derived here so there is still exactly one definition of it, and so this file can stay
 * pure. `lines` is that product's cart lines, normally straight from `productCartLines`.
 */
export function routeSupplementTileIntent<T extends SupplementTileLine>(
  intent: SupplementTileIntent,
  hasSupplements: boolean,
  lines: T[]
): SupplementTileAction<T> {
  // No add-ons: nothing to disambiguate, so the tile's own handler is already correct. This is what
  // makes the whole file a no-op for every venue that never enabled add-ons.
  if (!hasSupplements) {
    return { kind: 'fallback' };
  }

  if (lines.length === 0) {
    // Not in the cart: there is no selection to adjust, so ask for one. Only `+` can reach here —
    // the stepper is not rendered at zero, which is why the other two intents resolve to nothing
    // rather than to the fallback handler (that would decrement a line that does not exist).
    return intent === 'increment' ? { kind: 'openSheet' } : { kind: 'none' };
  }

  if (lines.length === 1) {
    // Exactly one selection in the cart, so "+1 of this dish" is unambiguous. Adjusting that line
    // directly is the friction this whole feature exists to remove: making the guest re-open a
    // sheet to re-confirm the choice they already made.
    const delta = intent === 'increment' ? 1 : intent === 'decrement' ? -1 : 'reset';
    return { kind: 'adjustLine', line: lines[0], delta };
  }

  // Two or more selections: the tile cannot know which one the guest means, and guessing is what
  // silently creates a second line. Ask. Deliberately also for `−` and reset — the ambiguity is
  // identical in both directions, and the sheet's "in your order" list answers all three.
  return { kind: 'openSheet' };
}
